import React from 'react';

import {
  Alert, Stack, Typography,
} from '@mui/material';

import ContentCard from '../../components/ContentCard';
import Link from '../../components/Link';
import SkeletonStack from '../../components/SkeletonStack';
import useUserPermissions from '../../utils/react/hooks/useUserPermissions';

interface PrelimMockExamBookingPageProps {
  title: string;
  qualifyingPermission: string;
  currentCourseLabel: string;
  futureCourseLabel: string;
  videoRunThroughHref: string;
  internalBookingComponent?: React.ReactElement | React.ReactElement[],
  externalBookingComponent: React.ReactElement | React.ReactElement[],
}

const PrelimMockExamBookingPage = ({
  title,
  qualifyingPermission,
  currentCourseLabel,
  futureCourseLabel,
  videoRunThroughHref,
  internalBookingComponent,
  externalBookingComponent,
}: PrelimMockExamBookingPageProps) => {
  const permissions = useUserPermissions();

  if (!permissions) {
    return <SkeletonStack />;
  }

  // Use tutorials as a proxy measurement of whether the student is currently enrolled or not
  // It's done this way to include accelerated students as well
  if (permissions.includes(qualifyingPermission) && internalBookingComponent) {
    return (
      <ContentCard
        title={title}
        variant="outlined"
        backButtonHref={-1}
      >
        <Stack spacing={2}>
          <Alert severity="success">
            It seems like you are currently enrolled in {currentCourseLabel}.
            Thank you for studying at Cognito!
          </Alert>
          <Typography variant="body2">
            Because you are currently enrolled, you get to sit
            a <b>longer</b>, <b>marked</b> version of the mock exam for FREE,
            as well as a <b><Link target="_blank" href={videoRunThroughHref}>video runthrough</Link></b> of every question!
          </Typography>
          {internalBookingComponent}
        </Stack>
      </ContentCard>
    );
  }

  return (
    <ContentCard
      title={title}
      variant="outlined"
      backButtonHref={-1}
    >
      <Stack spacing={2}>
        <Alert severity="error">
          It doesn't seems like you are currently enrolled in {currentCourseLabel}.
          Give the FREE mock exam a try and see if we might be a good fit for {futureCourseLabel}!
        </Alert>
        <Typography variant="body2">
          As a non-enrolled student, you get to sit a 1-hour mock exam and go
          through it with one of our star tutors right afterwards!
        </Typography>
        {externalBookingComponent}
      </Stack>
    </ContentCard>
  );
};

export const PrelimChemistryMockExamBookingPage = () => (
  <PrelimMockExamBookingPage
    title="Prelim Chemistry Mock Exam"
    qualifyingPermission="CPM_TUT_CHEM_11"
    currentCourseLabel="Prelim Chemistry"
    futureCourseLabel="HSC Chemistry"
    videoRunThroughHref="/video/chem/88dba218-8b49-484c-ac38-f7b1c8a4830c"
    internalBookingComponent={(
      <>
        <iframe src="https://app.acuityscheduling.com/schedule.php?owner=29834754&appointmentType=67048726&ref=embedded_csp" title="Schedule Appointment" width="100%" height="1200" frameBorder="0" /><script src="https://embed.acuityscheduling.com/js/embed.js" type="text/javascript" />
      </>
    )}
    externalBookingComponent={(
      <>
        <iframe src="https://app.acuityscheduling.com/schedule.php?owner=29834754&appointmentType=67048808&ref=embedded_csp" title="Schedule Appointment" width="100%" height="1200" frameBorder="0" /><script src="https://embed.acuityscheduling.com/js/embed.js" type="text/javascript" />
      </>
    )}
  />
);

export const PrelimPhysicsMockExamBookingPage = () => (
  <PrelimMockExamBookingPage
    title="Prelim Physics Mock Exam"
    qualifyingPermission="CPM_TUT_PHYS_11"
    currentCourseLabel="Prelim Physics"
    futureCourseLabel="HSC Physics"
    videoRunThroughHref="/video/phys/e7fcd791-96ee-4e2a-a42f-1f68b90163b2"
    internalBookingComponent={(
      <>
        <iframe src="https://app.acuityscheduling.com/schedule.php?owner=29834754&appointmentType=67048745&ref=embedded_csp" title="Schedule Appointment" width="100%" height="1200" frameBorder="0" /><script src="https://embed.acuityscheduling.com/js/embed.js" type="text/javascript" />
      </>
    )}
    externalBookingComponent={(
      <>
        <iframe src="https://app.acuityscheduling.com/schedule.php?owner=29834754&appointmentType=67048825&ref=embedded_csp" title="Schedule Appointment" width="100%" height="1200" frameBorder="0" /><script src="https://embed.acuityscheduling.com/js/embed.js" type="text/javascript" />
      </>
    )}
  />
);

export const PrelimMathsExt1MockExamBookingPage = () => (
  <PrelimMockExamBookingPage
    title="Prelim Maths Ext 1 Mock Exam"
    qualifyingPermission="CPM_TUT_MTX1_11"
    currentCourseLabel="Prelim Maths Ext 1"
    futureCourseLabel="HSC Maths Ext 1"
    videoRunThroughHref="/"
    externalBookingComponent={(
      <>
        <iframe src="https://app.acuityscheduling.com/schedule.php?owner=29834754&appointmentType=67048837&ref=embedded_csp" title="Schedule Appointment" width="100%" height="1200" frameBorder="0" /><script src="https://embed.acuityscheduling.com/js/embed.js" type="text/javascript" />
      </>
    )}
  />
);
